import { Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { CustomAvatar } from 'src/components/custom-avatar';
import { RHFAutocomplete } from 'src/components/hook-form';
import { translateKey } from 'src/locales/translate';
import { useWarehouseStockEnabled } from 'src/sections/@dashboard/warehouse/hooks/useClinicalStockProducts';
import { GetMedicineDto } from 'woofwoof-api';
import MedicineDetailsPanel from '../MedicineDetailsPanel';
import { renderMedicineSearchOption } from './MedicineSearchOptionRow';
import MedicineStockSummary from './MedicineStockSummary';
import { SPAN_FULL } from './medicationFormLayout';
import { MedicationsFormModalValuesProps } from './medicationFormSchema';
import {
  MedicineSearchOption,
  isSameMedicineOption,
  keepServerOrder,
  medicineOptionGroup,
  medicineOptionLabel,
  medicineOptionName,
  warehouseGroupIsComplete,
} from './medicineSearchOptions';
import { useMedicineSearchOptions } from './useMedicineSearchOptions';

interface MedicationIdentitySectionProps {
  disabled?: boolean;
  /**
   * #692 §4: a treatment line being edited keeps its drug. The name is shown
   * but not searched; changing the drug means a new line.
   */
  locked?: boolean;
  /** Who added this medication, once it exists. Absent while adding one. */
  creatorName?: string;
  creatorOrganizationName?: string;
  creatorAvatar?: string;
  onMedicineChange: (medicine: GetMedicineDto | null) => void;
  selectedMedicine: GetMedicineDto | null;
  /** Facility screens (#690): the clinic's own drugs lead the search. */
  stockEnabled?: boolean;
  /** Opens "Zgłoś zapotrzebowanie" for a medicine the Warehouse does not hold (#690). */
  onRequestNamedDemand?: (name: string) => void;
  /** #692: an explicit pick, for the dosage autofill; never a loaded or typed name. */
  onPick?: (option: MedicineSearchOption) => void;
  /** #692: the name was typed over or cleared, so what the last pick prefilled no longer fits. */
  onIdentityCleared?: () => void;
}

/**
 * Which medication this is: the medicine search (on Facility screens the
 * clinic's Warehouse first, #690, then the RPL registry), plus free text for
 * anything neither carries, with the leaflet under the field it belongs to.
 * On every other step the modal shows it above the rail instead.
 */
export default function MedicationIdentitySection({
  disabled,
  locked = false,
  creatorName,
  creatorOrganizationName,
  creatorAvatar,
  onMedicineChange,
  selectedMedicine,
  stockEnabled = false,
  onRequestNamedDemand,
  onPick,
  onIdentityCleared,
}: MedicationIdentitySectionProps) {
  const { setValue, getValues, watch } =
    useFormContext<MedicationsFormModalValuesProps>();
  const [medicineInputValue, setMedicineInputValue] = useState('');
  const warehouseEnabled = useWarehouseStockEnabled();
  // The stock section's own gate: the owner's tab and a disabled Warehouse
  // keep the registry-only search.
  const warehouseAware = stockEnabled && warehouseEnabled;
  const { options, isLoading } = useMedicineSearchOptions({
    query: medicineInputValue,
    warehouseAware,
  });
  const stockProductId = watch('stockProductId');
  // R6 keeps the Product in step with the drug. Switching "Pobierz z
  // magazynu" off keeps it too, and the Facility still holds that drug.
  const summaryProductId = warehouseAware ? stockProductId : '';
  // The registry group leaves out what the Warehouse group lists, so only a
  // pick from it says the drug was not found — and only when that group was
  // not cut at the server's cap (R13). A loaded, typed or cleared name says
  // nothing.
  const [missingFromWarehouse, setMissingFromWarehouse] = useState<
    string | null
  >(null);
  const showStockSummary =
    warehouseAware &&
    (Boolean(summaryProductId) || Boolean(missingFromWarehouse));

  const clearIdentity = (): void => {
    setValue('medicineProductId', undefined);
    setValue('preparationType', undefined);
    onMedicineChange(null);
  };

  // R6: a picked Warehouse Product no longer matches once the drug moves to
  // something the search didn't vouch for; leaving it would take the wrong
  // stock on save and print the wrong drug on the legal record card.
  // R12: only a Product taken in this form session. A saved taking is a
  // posted Stock Issue that clearing would reverse on save, unseen (and 422
  // with the Warehouse disabled); "Pobierz z magazynu" alone switches it off.
  const clearStockIfSet = (): void => {
    if (!warehouseAware || getValues('stockWasSet')) return;
    if (!getValues('stockProductId')) return;
    setValue('stockEnabled', false);
    setValue('stockProductId', '');
    setValue('stockBatchId', '');
  };

  // Only an explicit pick lands here; loading a saved Medication never picks,
  // so an edit keeps its own stock.
  const applyPick = (option: MedicineSearchOption): void => {
    setValue('medicineProductId', option.medicine?.productId);
    setValue('preparationType', option.medicine?.preparationType);
    onMedicineChange(option.medicine);
    setMissingFromWarehouse(
      option.source === 'REGISTRY' &&
        warehouseAware &&
        warehouseGroupIsComplete(options)
        ? option.medicine.productName
        : null,
    );
    if (option.source === 'WAREHOUSE') {
      // The drug is searched for once: "Pobierz z magazynu" takes this
      // Product and FEFO chooses the Batch — except for the Product a saved
      // taking already holds, whose Batch stays so nothing is re-issued.
      const keepsSavedTaking =
        getValues('stockWasSet') &&
        getValues('stockProductId') === option.product.id;
      setValue('stockEnabled', true);
      setValue('stockProductId', option.product.id);
      if (!keepsSavedTaking) setValue('stockBatchId', '');
    } else {
      clearStockIfSet();
    }
    onPick?.(option);
  };

  return (
    <Stack sx={{ ...SPAN_FULL, gap: 2.5 }}>
      {/* Provenance used to be a table column. It belongs with the record it
          describes, and only exists once the record does. */}
      {(creatorName || creatorOrganizationName) && (
        <Stack sx={{ gap: 0.75 }} data-testid="medication-added-by">
          <Typography variant="caption" color="text.secondary">
            {translateKey('medication.table.addedBy')}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <CustomAvatar
              src={creatorAvatar}
              name={creatorName}
              alt={creatorName}
              sx={{ width: 44, height: 44 }}
            />
            <Stack sx={{ minWidth: 0 }}>
              {creatorName && (
                <Typography variant="subtitle2" noWrap>
                  {creatorName}
                </Typography>
              )}
              {creatorOrganizationName && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {creatorOrganizationName}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Stack>
      )}

      <RHFAutocomplete<MedicineSearchOption | string, false, false, true>
        name="name"
        label="Nazwa lekarstwa"
        required
        helperText={
          locked ? (
            <span data-testid="medication-medicine-locked">
              {translateKey('medication.form.medicineLockedHint')}
            </span>
          ) : undefined
        }
        disabled={disabled || locked}
        freeSolo
        options={options}
        loading={isLoading}
        groupBy={warehouseAware ? medicineOptionGroup : undefined}
        // The server already matched; a Product may match only through its
        // registry link, which MUI's own filter would hide.
        filterOptions={warehouseAware ? keepServerOrder : undefined}
        getOptionLabel={medicineOptionLabel}
        isOptionEqualToValue={isSameMedicineOption}
        onChange={(event, newValue) => {
          if (typeof newValue === 'string') {
            clearIdentity();
            clearStockIfSet();
            setMissingFromWarehouse(null);
            onIdentityCleared?.();
            return newValue;
          }
          if (!newValue) {
            onMedicineChange(null);
            clearStockIfSet();
            setMissingFromWarehouse(null);
            onIdentityCleared?.();
            return '';
          }
          applyPick(newValue);
          return medicineOptionName(newValue);
        }}
        // Typing is the commit. MUI's freeSolo otherwise only takes the text on
        // Enter and drops it on blur, so a name off the registry never survived
        // clicking "Dalej". `autoSelect` would fix that too, but it prefers a
        // merely *hovered* option over what was typed — and the popup sits over
        // the button, so the pointer crosses it on the way out.
        onInputChange={(event, newInputValue, reason) => {
          setMedicineInputValue(newInputValue);
          // `reset` follows picking an option and `clear` follows the ✕; both
          // would overwrite what onChange just set.
          if (reason !== 'input') return;
          setValue('name', newInputValue);
          clearIdentity();
          clearStockIfSet();
          setMissingFromWarehouse(null);
          onIdentityCleared?.();
        }}
        renderOption={renderMedicineSearchOption}
        noOptionsText={translateKey('medication.form.noMedicineFound')}
      />

      <MedicineDetailsPanel
        medicine={selectedMedicine}
        stockSummary={
          showStockSummary ? (
            <MedicineStockSummary
              productId={summaryProductId}
              missingMedicineName={missingFromWarehouse ?? undefined}
              onRequestDemand={onRequestNamedDemand}
            />
          ) : undefined
        }
      />
    </Stack>
  );
}
